import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { CardContent, Typography } from '@mui/material';

import { FilterDateFields } from 'components/common/FilterDateFields';
import { axiosWithAuthHeader } from 'core/http';
import { DASHBOARD_RISKS } from 'core/http/endpoints';
import { EntityDashboardType } from 'types/dashboard/EntityDashboardType';
import { StatisticHistogram } from 'components/common/StatisticHistogram/StatisticHistogram';
import { UILoader } from 'components/UI/UILoader';
import { DonutDiagram } from 'components/common/DonutDiagram';
import styles from '../Reports.module.scss';
import { UsersTableStatistic } from './UsersTableStatistic';

const RisksReports = () => {
    const [dateTo, setDateTo] = useState<Dayjs | null>(null);
    const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
    const [data, setData] = useState<EntityDashboardType>();

    useEffect(() => {
        const params = {
            dateFrom: dateFrom ? dayjs(dateFrom)?.startOf('day').format('YYYY-MM-DD HH:mm:ss') : null,
            dateTo: dateTo ? dayjs(dateTo)?.endOf('day').format('YYYY-MM-DD HH:mm:ss') : null,
        };

        axiosWithAuthHeader
            .get<EntityDashboardType>(DASHBOARD_RISKS, { params })
            .then((res) => {
                setData(res.data);
            })
            .catch((error) => console.log(error));
    }, [dateFrom, dateTo]);

    const riskStatistic = useMemo(() => {
        if (!data) return [];

        return [
            { title: 'Средняя продолжительность работы с риском:', value: data.averageWorkTime },
            { title: 'Создано рисков:', value: data.createdAmount },
            { title: 'Утверждено рисков:', value: data.approvedAmount },
        ];
    }, [data]);

    const diagramSeries = useMemo(() => {
        if (!data) return [];
        return data?.objectsByType.map((obj) => +obj.count);
    }, [data]);

    const diagramLabels = useMemo(() => {
        if (!data) return [];
        return data?.objectsByType.map((obj) => obj.statusName);
    }, [data]);

    return (
        <div>
            <FilterDateFields
                setDateFrom={setDateFrom}
                setDateTo={setDateTo}
                dateTo={dateTo}
                dateFrom={dateFrom}
                showFilterButton={false}
                title={'Выберите период:'}
            />

            {data ? (
                <div className={styles.container}>
                    <div className={styles.cardsContainer}>
                        {riskStatistic.map((data, index) => (
                            <CardContent key={index} className={styles.card}>
                                <Typography variant="body2" color="text.secondary" className={styles.cardTitle}>
                                    {data.title}
                                </Typography>
                                <div className={styles.cardStatistic}>
                                    <Typography variant="h5" component="div">
                                        {data.value || '0'}
                                    </Typography>
                                </div>
                            </CardContent>
                        ))}
                    </div>
                    <div className={styles.cardsContainer}>
                        <StatisticHistogram
                            name={'Риски'}
                            data={data.objectsByStatus.map((data) => +data.count)}
                            categories={data.objectsByStatus.map((data) => data.statusName)}
                        />
                        <DonutDiagram
                            name={'Источник созникновения риска'}
                            series={diagramSeries}
                            labels={diagramLabels}
                        />
                        <UsersTableStatistic data={data.objectsByUser} name={'Сотрудники, создавшие риски:'} />
                    </div>
                </div>
            ) : (
                <UILoader isVisible />
            )}
        </div>
    );
};

export { RisksReports };
